import { z } from "zod";
import { canonicalSha256 } from "./canonicalize.js";
import { protocolIdSchema, protocolTimestampSchema, protocolVersionSchema, sha256DigestSchema, UNIVERSAL_VERIFICATION_PROTOCOL, } from "./identifiers.js";
export const occurrenceBoundarySignalSchema = z
    .object({
    id: protocolIdSchema,
    kind: z.enum([
        "explicit_start",
        "explicit_finish",
        "process_start",
        "process_exit",
        "session_open",
        "session_close",
        "task_association",
        "repository_state",
        "branch_worktree",
        "external_effect",
        "worker_done_claim",
        "host_lifecycle_close",
        "session_compacted",
        "agent_run_host",
        "timeout",
        "abandonment",
    ]),
    taskId: protocolIdSchema.nullable(),
    repositoryDigest: sha256DigestSchema.nullable(),
    worktreeId: protocolIdSchema.nullable(),
    branch: z.string().trim().min(1).nullable(),
    actorId: protocolIdSchema,
    sessionId: protocolIdSchema.nullable(),
    executionContextDigest: sha256DigestSchema.nullable(),
    observedAt: protocolTimestampSchema,
})
    .strict();
export const workOccurrenceBoundarySchema = z
    .object({
    protocolVersion: protocolVersionSchema,
    id: protocolIdSchema,
    tenantId: protocolIdSchema,
    stableOccurrenceDigest: sha256DigestSchema,
    signalIds: z.array(protocolIdSchema).min(1),
    taskIds: z.array(protocolIdSchema),
    repositoryDigests: z.array(sha256DigestSchema),
    actorIds: z.array(protocolIdSchema).min(1),
    sessionIds: z.array(protocolIdSchema),
    startAt: protocolTimestampSchema.nullable(),
    endAt: protocolTimestampSchema.nullable(),
    endReason: z
        .enum(["explicit_close", "host_lifecycle", "process_exit", "timeout", "abandoned"])
        .nullable(),
    confidence: z.enum(["high", "medium", "low", "ambiguous"]),
    reasons: z.array(z.string().trim().min(1)).min(1),
    state: z.enum(["BOUND", "PROVISIONAL", "ABSTAIN"]),
    successClaimedByWorker: z.boolean(),
    successfulCompletionEstablished: z.literal(false),
    eligibleForAutomaticContractApplication: z.boolean(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.confidence === "ambiguous" && value.eligibleForAutomaticContractApplication) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["eligibleForAutomaticContractApplication"],
            message: "ambiguous boundaries cannot apply contracts automatically",
        });
    }
    if (value.successClaimedByWorker && value.successfulCompletionEstablished !== false) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["successfulCompletionEstablished"],
            message: "worker done claims cannot establish successful completion",
        });
    }
});
export const occurrenceCorrectionSchema = z
    .object({
    protocolVersion: protocolVersionSchema,
    id: protocolIdSchema,
    occurrenceIds: z.array(protocolIdSchema).min(1),
    action: z.enum(["split", "join", "relabel"]),
    correctedByActorId: protocolIdSchema,
    correctedAt: protocolTimestampSchema,
    reason: z.string().trim().min(1),
    affects: z.enum(["future_inference_only", "history_reopened"]),
    explicitHistoryReviewId: protocolIdSchema.nullable(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.affects === "history_reopened" && value.explicitHistoryReviewId === null) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["explicitHistoryReviewId"],
            message: "history correction requires an explicit review id",
        });
    }
});
export const occurrenceRuntimeObservationSchema = z
    .object({
    id: protocolIdSchema,
    kind: z.enum([
        "lifecycle-task-opened",
        "adapter-process-started",
        "adapter-process-exited",
        "session-attached",
        "session-detached",
        "worker-claimed-done",
        "external-effect-recorded",
        "host-closed",
        "session-compacted",
        "agent-run-hosted",
        "timeout-emitted",
        "abandonment-detected",
    ]),
    taskId: protocolIdSchema.nullable(),
    repositoryDigest: sha256DigestSchema.nullable(),
    worktreeId: protocolIdSchema.nullable(),
    branch: z.string().trim().min(1).nullable(),
    actorId: protocolIdSchema,
    sessionId: protocolIdSchema.nullable(),
    executionContextDigest: sha256DigestSchema.nullable(),
    observedAt: protocolTimestampSchema,
    adapterRunId: protocolIdSchema.nullable(),
})
    .strict();
function uniqueSorted(values) {
    return [...new Set(values.filter((value) => value !== null))].sort();
}
/**
 * Order observations by the INSTANT they name, then by id.
 *
 * `protocolTimestampSchema` is `z.string().datetime({ offset: true })`, so
 * `2026-09-13T02:00:00.000-08:00` and `2026-09-13T10:00:00.000Z` are the same
 * moment written two ways. Comparing the strings puts the first one earlier
 * and makes `stableOccurrenceDigest` (a hash over this order) depend on how
 * the timestamp was written. `Date.parse` compares the moment instead.
 *
 * The id tiebreak makes the order total. Without it the order of two signals
 * sharing a timestamp is the order the caller happened to pass them in, and
 * `stableOccurrenceDigest` — which hashes the signals in this order — is not
 * stable: the same set of signals hashes to two different digests.
 */
function byObservedInstantThenId(left, right) {
    const byInstant = Date.parse(left.observedAt) - Date.parse(right.observedAt);
    if (byInstant !== 0)
        return byInstant;
    return left.id.localeCompare(right.id);
}
function stableOccurrenceDigest(input) {
    const content = {
        tenantId: input.tenantId,
        signals: input.signals.map((signal) => ({
            kind: signal.kind,
            taskId: signal.taskId,
            repositoryDigest: signal.repositoryDigest,
            worktreeId: signal.worktreeId,
            branch: signal.branch,
            actorId: signal.actorId,
            sessionId: signal.sessionId,
            executionContextDigest: signal.executionContextDigest,
            observedAt: signal.observedAt,
        })),
    };
    return canonicalSha256(content, input.sha256);
}
function isStartShapedSignal(signal) {
    // Three kinds authorise a start. `explicit_start` and `session_open` are the
    // declared ones; `agent_run_host` is the hosted one — a run the host itself
    // observed opening. `process_start` is deliberately NOT among them: a process
    // appearing is evidence that something ran, not that this occurrence began.
    return (signal.kind === "explicit_start" ||
        signal.kind === "session_open" ||
        signal.kind === "agent_run_host");
}
function startFromSignals(signals) {
    // Callers pass signals already ordered by observed instant, then id, so the
    // first start-shaped row is the earliest start. Absence stays null: a close,
    // timeout, process_start, or effect is not a start and must not become one.
    const start = signals.find(isStartShapedSignal);
    return start === undefined ? null : start.observedAt;
}
function endFromSignals(signals) {
    const explicit = signals.find((signal) => signal.kind === "explicit_finish" || signal.kind === "session_close");
    if (explicit)
        return { endAt: explicit.observedAt, endReason: "explicit_close" };
    const host = signals.find((signal) => signal.kind === "host_lifecycle_close");
    if (host)
        return { endAt: host.observedAt, endReason: "host_lifecycle" };
    const processExit = signals.find((signal) => signal.kind === "process_exit");
    if (processExit)
        return { endAt: processExit.observedAt, endReason: "process_exit" };
    const timeout = signals.find((signal) => signal.kind === "timeout");
    if (timeout)
        return { endAt: timeout.observedAt, endReason: "timeout" };
    const abandoned = signals.find((signal) => signal.kind === "abandonment");
    if (abandoned)
        return { endAt: abandoned.observedAt, endReason: "abandoned" };
    return { endAt: null, endReason: null };
}
function confidenceFor(signals) {
    const reasons = [];
    const executionContextResolved = signals.every((signal) => signal.kind !== "session_open" || signal.executionContextDigest !== null);
    const taskIds = uniqueSorted(signals.map((signal) => signal.taskId));
    const repositoryDigests = uniqueSorted(signals.map((signal) => signal.repositoryDigest));
    const worktreeIds = uniqueSorted(signals.map((signal) => signal.worktreeId));
    const sessionIds = uniqueSorted(signals.map((signal) => signal.sessionId));
    const hasClose = endFromSignals(signals).endAt !== null;
    const doneOnly = signals.some((signal) => signal.kind === "worker_done_claim") &&
        !signals.some((signal) => [
            "explicit_finish",
            "session_close",
            "host_lifecycle_close",
            "timeout",
            "abandonment",
        ].includes(signal.kind));
    if (!executionContextResolved) {
        reasons.push("session-open-without-execution-context");
        return { confidence: "ambiguous", state: "ABSTAIN", reasons };
    }
    if (taskIds.length > 1 || repositoryDigests.length > 1 || worktreeIds.length > 1) {
        reasons.push("conflicting-task-or-repository-boundary");
        return { confidence: "ambiguous", state: "ABSTAIN", reasons };
    }
    // Two distinct sessions are two occurrences. Unioning them produces one
    // boundary whose `sessionIds` names both, and nothing downstream reads that
    // array, so the merge is invisible where it matters: such a boundary was
    // reaching `high`/`BOUND` and `eligibleForAutomaticContractApplication`.
    if (sessionIds.length > 1) {
        reasons.push("conflicting-session-boundary");
        return { confidence: "ambiguous", state: "ABSTAIN", reasons };
    }
    // These three kinds exist to name identity. An instance that names none is
    // incomplete evidence, not a silent extra row.
    if (signals.some((signal) => signal.kind === "task_association" && signal.taskId === null)) {
        reasons.push("task-association-without-task");
        return { confidence: "ambiguous", state: "ABSTAIN", reasons };
    }
    if (signals.some((signal) => signal.kind === "repository_state" && signal.repositoryDigest === null)) {
        reasons.push("repository-state-without-digest");
        return { confidence: "ambiguous", state: "ABSTAIN", reasons };
    }
    if (signals.some((signal) => signal.kind === "branch_worktree" && signal.branch === null && signal.worktreeId === null)) {
        reasons.push("branch-worktree-without-workspace-identity");
        return { confidence: "ambiguous", state: "ABSTAIN", reasons };
    }
    // `branch` is digest payload on other kinds. `branch_worktree` is the signal
    // that makes it a boundary key.
    if (signals.some((signal) => signal.kind === "branch_worktree") &&
        uniqueSorted(signals.map((signal) => signal.branch)).length > 1) {
        reasons.push("conflicting-branch-worktree-boundary");
        return { confidence: "ambiguous", state: "ABSTAIN", reasons };
    }
    if (doneOnly) {
        reasons.push("worker-done-claim-is-segmentation-signal-only");
        return { confidence: "low", state: "PROVISIONAL", reasons };
    }
    if (!hasClose) {
        // Compaction rewrites a live session; it does not end the occurrence.
        if (signals.some((signal) => signal.kind === "session_compacted")) {
            reasons.push("session-compacted-preserves-open-continuity");
        }
        reasons.push("no-explicit-close-timeout-or-abandonment");
        return { confidence: "low", state: "PROVISIONAL", reasons };
    }
    if (signals.some((signal) => signal.kind === "timeout" || signal.kind === "abandonment")) {
        reasons.push("typed-timeout-or-abandonment-ended-occurrence");
        return { confidence: "medium", state: "PROVISIONAL", reasons };
    }
    if (signals.some((signal) => signal.kind === "process_exit") &&
        !signals.some((signal) => signal.kind === "explicit_finish" ||
            signal.kind === "session_close" ||
            signal.kind === "host_lifecycle_close")) {
        reasons.push("process-exit-ended-occurrence-without-success-proof");
        return { confidence: "medium", state: "PROVISIONAL", reasons };
    }
    // The reason below claims an explicit start. Nothing had checked that any
    // start signal existed, so a set carrying only a `process_start` — or no
    // start-shaped signal at all — reached `high`/`BOUND` and, through it,
    // `eligibleForAutomaticContractApplication`, under a reason that named
    // evidence it did not have.
    if (!signals.some(isStartShapedSignal)) {
        if (signals.some((signal) => signal.kind === "process_start")) {
            reasons.push("process-start-is-not-an-occurrence-start");
        }
        reasons.push("no-declared-or-hosted-start-signal");
        return { confidence: "medium", state: "PROVISIONAL", reasons };
    }
    reasons.push("explicit-start-and-close-with-resolved-context");
    return { confidence: "high", state: "BOUND", reasons };
}
export function inferWorkOccurrenceBoundary(input) {
    const signals = input.signals
        .map((signal) => occurrenceBoundarySignalSchema.parse(signal))
        .sort(byObservedInstantThenId);
    if (signals.length === 0) {
        throw new Error("occurrence inference requires at least one boundary signal");
    }
    const { endAt, endReason } = endFromSignals(signals);
    const boundary = confidenceFor(signals);
    return workOccurrenceBoundarySchema.parse({
        protocolVersion: UNIVERSAL_VERIFICATION_PROTOCOL,
        id: input.id,
        tenantId: input.tenantId,
        stableOccurrenceDigest: stableOccurrenceDigest({
            tenantId: input.tenantId,
            signals,
            sha256: input.sha256,
        }),
        signalIds: signals.map((signal) => signal.id),
        taskIds: uniqueSorted(signals.map((signal) => signal.taskId)),
        repositoryDigests: uniqueSorted(signals.map((signal) => signal.repositoryDigest)),
        actorIds: uniqueSorted(signals.map((signal) => signal.actorId)),
        sessionIds: uniqueSorted(signals.map((signal) => signal.sessionId)),
        startAt: startFromSignals(signals),
        endAt,
        endReason,
        confidence: boundary.confidence,
        reasons: boundary.reasons,
        state: boundary.state,
        successClaimedByWorker: signals.some((signal) => signal.kind === "worker_done_claim"),
        successfulCompletionEstablished: false,
        eligibleForAutomaticContractApplication: boundary.state === "BOUND" && boundary.confidence === "high",
    });
}
function concurrentOccurrenceGroupKey(signal) {
    if (signal.taskId !== null && signal.repositoryDigest !== null && signal.worktreeId !== null) {
        return [signal.taskId, signal.repositoryDigest, signal.worktreeId].join("\u001f");
    }
    // Absence of task, repository, and worktree is not a shared identity.
    // Keying those as `task:unknown|repo:unknown|worktree:unknown` collapsed
    // unrelated sessions into one occurrence. Discriminate by session, then
    // actor — actorId is required, so the fallback is total.
    return [
        signal.taskId ?? "task:unknown",
        signal.repositoryDigest ?? "repo:unknown",
        signal.worktreeId ?? "worktree:unknown",
        signal.sessionId ?? signal.actorId,
    ].join("\u001f");
}
export function splitConcurrentOccurrenceSignals(input) {
    const groups = new Map();
    for (const signal of input.signals.map((candidate) => occurrenceBoundarySignalSchema.parse(candidate))) {
        const key = concurrentOccurrenceGroupKey(signal);
        groups.set(key, [...(groups.get(key) ?? []), signal]);
    }
    return [...groups.values()].map((signals, index) => inferWorkOccurrenceBoundary({
        id: `occurrence:${index + 1}`,
        tenantId: input.tenantId,
        signals,
        sha256: input.sha256,
    }));
}
export function createOccurrenceCorrection(input) {
    return occurrenceCorrectionSchema.parse({
        protocolVersion: UNIVERSAL_VERIFICATION_PROTOCOL,
        id: input.id,
        occurrenceIds: [...input.occurrenceIds],
        action: input.action,
        correctedByActorId: input.correctedByActorId,
        correctedAt: input.correctedAt,
        reason: input.reason,
        affects: input.explicitHistoryReviewId ? "history_reopened" : "future_inference_only",
        explicitHistoryReviewId: input.explicitHistoryReviewId ?? null,
    });
}
function signalKindForRuntimeObservation(kind) {
    switch (kind) {
        case "lifecycle-task-opened":
            return "explicit_start";
        case "adapter-process-started":
            return "process_start";
        case "adapter-process-exited":
            return "process_exit";
        case "session-attached":
            return "session_open";
        case "session-detached":
            return "session_close";
        case "worker-claimed-done":
            return "worker_done_claim";
        case "external-effect-recorded":
            return "external_effect";
        case "host-closed":
            return "host_lifecycle_close";
        case "session-compacted":
            return "session_compacted";
        case "agent-run-hosted":
            return "agent_run_host";
        case "timeout-emitted":
            return "timeout";
        case "abandonment-detected":
            return "abandonment";
    }
}
export function projectRuntimeOccurrenceSignals(input) {
    return input.observations
        .map((candidate) => occurrenceRuntimeObservationSchema.parse(candidate))
        .sort(byObservedInstantThenId)
        .map((observation) => occurrenceBoundarySignalSchema.parse({
        id: `signal:${observation.id}`,
        kind: signalKindForRuntimeObservation(observation.kind),
        taskId: observation.taskId,
        repositoryDigest: observation.repositoryDigest,
        worktreeId: observation.worktreeId,
        branch: observation.branch,
        actorId: observation.actorId,
        sessionId: observation.sessionId,
        executionContextDigest: observation.executionContextDigest,
        observedAt: observation.observedAt,
    }));
}
